/**
 * QuotationsHub — iter-290 (Feb 2026)
 * ===================================
 * Entry hub under: Dashboard → CLOUDGM FZ LLC (Supervision) →
 *                   CLOUDGM FZ LLC Management → عروض الأسعار
 *
 * Two action cards:
 *   1) Create a new quotation
 *   2) Manage / follow existing quotations
 *
 * Route: /manage/cloudgm-fz-llc/quotations
 */
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, ListChecks, ChevronLeft, ChevronRight } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { usePerms } from '../../permissions/PermissionsContext';

export default function QuotationsHub() {
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const Arrow = isAr ? ChevronLeft : ChevronRight;

  const CARDS = [
    {
      id: 'create',
      titleAr: 'إنشاء عرض أسعار',
      titleEn: 'Create Quotation',
      descAr:  'عرض سعر جديد بالتنسيق الرسمي للشركة مع الخدمات والرسوم والشروط',
      descEn:  'Compose a new quotation on CloudGM letterhead with services, fees and terms',
      icon:    Plus,
      color:   '#10b981',
      route:   '/manage/cloudgm-fz-llc/quotations/create',
    },
    {
      id: 'manage',
      titleAr: 'متابعة عروض الأسعار',
      titleEn: 'Manage Quotations',
      descAr:  'عرض كل عروض الأسعار مع إمكانية التعديل والحذف والطباعة',
      descEn:  'List, edit, delete and print every quotation',
      icon:    ListChecks,
      color:   '#3B9EDC',
      route:   '/manage/cloudgm-fz-llc/quotations/list',
    },
  ];

  return (
    <DashboardLayout
      title="عروض الأسعار"
      titleEn="Price Quotations"
      subtitle="إنشاء ومتابعة عروض أسعار CLOUDGM FZ LLC"
      subtitleEn="Create and track CLOUDGM FZ LLC price quotations"
      screenCode="FZLC-005"
      backPath="/manage/cloudgm-fz-llc"
      onBack={() => navigate('/manage/cloudgm-fz-llc')}
      breadcrumb={[
        { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
        { labelAr: 'CLOUDGM FZ LLC (للإشراف)', labelEn: 'CLOUDGM FZ LLC (Supervision)', path: '/dashboard' },
        { labelAr: 'إدارة شركة CLOUDGM FZ LLC', labelEn: 'CLOUDGM FZ LLC Management', path: '/manage/cloudgm-fz-llc' },
        { labelAr: 'عروض الأسعار', labelEn: 'Price Quotations' },
      ]}
    >
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
               data-testid="quotations-hub">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {visiblePages(CARDS, 'route').map((c, i) => (
            <motion.button
              key={c.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              whileHover={{ y: -5 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => navigate(c.route)}
              className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border transition-all text-start"
              style={{ borderColor: `${c.color}30` }}
              data-testid={`quotations-hub-card-${c.id}`}
            >
              <div className="flex items-start gap-4">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center border transition-transform group-hover:scale-110 shrink-0"
                  style={{ backgroundColor: `${c.color}15`, borderColor: `${c.color}40` }}
                >
                  <c.icon className="h-7 w-7" style={{ color: c.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                    {isAr ? c.titleAr : c.titleEn}
                    <Arrow className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity"
                           style={{ color: c.color }} />
                  </h3>
                  <p className="text-sm text-white/60 leading-relaxed">
                    {isAr ? c.descAr : c.descEn}
                  </p>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </section>
    </DashboardLayout>
  );
}
